import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { User as GqlUser, VinculoType } from '../generated/graphql-types';
import { apolloClient } from '../lib/apollo-client';
import {
  fetchUserCompleteData,
  getAccessToken,
  getCurrentUser,
  restoreActiveContextFromToken,
  logout as serviceLogout,
  switchContext,
  validateToken
} from '../services/auth/auth.service';
import { permissionCache } from '../services/auth/permission-cache.service';

// Estende o tipo User gerado para incluir campos do lado do cliente
export interface User extends GqlUser {
  isSuperAdmin?: boolean;
  activeVinculo?: VinculoType | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  hasPermission: (modulo: string, operacao: string) => boolean;
  hasRole: (roleName: string) => boolean;
  getActiveVinculos: () => VinculoType[];
  login: (user: User, accessToken: string, idToken: string) => void;
  logout: () => void;
  switchUserContext: (vinculoId: number) => Promise<void>;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cachedPermissions, setCachedPermissions] = useState<any>(null);

  // Verificação dinâmica de Super Admin através de vínculos
  const isSuperAdmin = useMemo(() => {
    if (!user) return false;
    
    // Verificar se o usuário tem flag isSuperAdmin
    if (user.isSuperAdmin) {

      return true;
    }
    
    // Verificar se algum vínculo tem role de sistema
    const hasSystemRole = user.vinculos?.some((vinculo) => {

      // Verificar se o role tem flag isSystemRole
      if ((vinculo.role as any)?.isSystemRole && vinculo.ativo) {

        return true;
      }
      
      return false;
    });
    
    if (hasSystemRole) {
      return true;
    }
    
    return false;
  }, [user]);

  // Verificação de permissão otimizada com cache
  const hasPermission = useCallback((modulo: string, operacao: string): boolean => {
    if (!user) return false;

    // Tentar via cache do backend (normalizado)
    if (cachedPermissions) {
      if (permissionCache.canAccess(cachedPermissions, modulo, operacao)) {
        return true;
      }
    }

    // Fallback via vínculos (normalizado)
    // Super Admin tem acesso total
    if (user.isSuperAdmin) return true;

    const normalizeOperation = (op: string) => {
      const value = (op || '').toString().toLowerCase();
      const map: Record<string, string> = {
        read: 'read', view: 'view', list: 'view',
        write: 'write', create: 'create', update: 'update', delete: 'delete',
        admin: 'admin', all: 'all', '*': '*'
      };
      return map[value] || value;
    };
    const normalizeModule = (m: string) => (m || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

    const targetModule = normalizeModule(modulo);
    const targetOp = normalizeOperation(operacao);

    return user.vinculos?.some((vinculo) => {
      if (!vinculo.ativo) return false;
      const permissoes = (vinculo.role as any)?.permissoes || [];
      return permissoes.some((rp: any) => {
        const p = rp?.permissao;
        if (!p || !p.ativo) return false;
        const mod = normalizeModule(p.modulo);
        const op = normalizeOperation(p.operacao);
        if (mod !== targetModule) return false;
        if (op === 'admin' || op === 'all' || op === '*') return true;
        return op === targetOp;
      });
    }) || false;
  }, [user, cachedPermissions]);

  // Verificação de role através de vínculos
  const hasRole = useCallback((roleName: string): boolean => {
    if (!user) return false;
    
    // Verificar se o activeVinculo tem o roleName
    if ((user.activeVinculo as any)?.roleName === roleName) return true;
    
    return user.vinculos?.some((vinculo) => 
      ((vinculo as any).roleName === roleName || vinculo.role?.nome === roleName) && vinculo.ativo
    );
  }, [user]);

  // Obter vínculos ativos
  const getActiveVinculos = useCallback((): VinculoType[] => {
    if (!user) return [];
    
    // Se é SUPER_ADMIN, retornar todos os vínculos (mesmo inativos) ou um vínculo virtual
    if (isSuperAdmin) {
      
      // Se tem vínculos, retornar todos
      if (user.vinculos && user.vinculos.length > 0) {
        return user.vinculos;
      }
      
      // Se não tem vínculos mas é SUPER_ADMIN, criar um vínculo virtual
      return [{
        __typename: 'VinculoType',
        id: 0,
        ativo: true,
        escola: null,
        funcionario: null,
        role: {
          __typename: 'RoleVinculoType',
          id: 1,
          nome: 'SUPER_ADMIN'
        },
        secretaria: null,
        user: {
          __typename: 'UserVinculoType',
          id: Number(user.id),
          email: user.email || '',
          username: user.username || ''
        }
      }] as VinculoType[];
    }
    
    return user.vinculos?.filter(vinculo => vinculo.ativo) || [];
  }, [user, isSuperAdmin]);

  // Função para carregar permissões com cache
  const loadPermissions = useCallback(async (userId: string | number) => {
    try {
      const permissions = await permissionCache.getPermissions(Number(userId));
      setCachedPermissions(permissions);
    } catch (error) {
      console.error('❌ Error loading cached permissions:', error);
      setCachedPermissions(null);
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        const currentUser = getCurrentUser();
        const accessToken = getAccessToken();

        if (currentUser && accessToken) {
          const isTokenValid = await validateToken();
          if (!isTokenValid) {
            logout();
            return;
          }

          // Carregar permissões com cache primeiro (Layer 1 & 2)
          if (currentUser.id) {
            await loadPermissions(String(currentUser.id));
          }

          // Sempre buscar os dados mais recentes do usuário para garantir que as permissões estejam atualizadas.
          try {
            const userData = await fetchUserCompleteData();
            if (userData) {
              // Restaurar contexto ativo do JWT se necessário
              const restoredUser = await restoreActiveContextFromToken(userData);
              setUser(restoredUser as User);
              // Recarregar permissões se o ID mudou
              if (userData.id && userData.id !== currentUser.id) {
                await loadPermissions(String(userData.id));
              }
            } else {
              // Restaurar contexto ativo do JWT para dados do localStorage
              const restoredUser = await restoreActiveContextFromToken(currentUser);
              setUser(restoredUser as User);
            }
          } catch (error: any) {
            console.error('❌ Erro ao carregar dados completos do usuário:', error);
            // Em caso de erro (ex: API offline), confia nos dados do localStorage para não quebrar a app
            // Mas ainda tenta restaurar o contexto ativo
            try {
              const restoredUser = await restoreActiveContextFromToken(currentUser);
              setUser(restoredUser as User);
            } catch {
              setUser(currentUser as User);
            }
          }
        } else {
          setUser(null);
          setCachedPermissions(null);
        }
      } catch (error) {
        console.error('❌ Erro ao verificar autenticação:', error);
        setUser(null);
        setCachedPermissions(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [loadPermissions]);

  const login = async (userData: User) => {
    setIsLoading(true);
    try {
      // Após o login, sempre buscar os dados mais recentes para garantir que o estado esteja completo
      const completeUserData = await fetchUserCompleteData();
      if (completeUserData) {
        setUser(completeUserData as User);
        // Carregar permissões após login bem-sucedido
        if (completeUserData.id) {
          await loadPermissions(String(completeUserData.id));
        }
      } else {
        setUser(userData);
        if (userData.id) {
          await loadPermissions(String(userData.id));
        }
      }
    } catch (error) {
      console.error('❌ Erro ao buscar dados completos após login:', error);
      // Em caso de erro, usa os dados básicos para não quebrar o login
      setUser(userData);
      if (userData.id) {
        await loadPermissions(String(userData.id));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    try {
      serviceLogout();
      permissionCache.clearAllCache(); // Limpar cache de permissões
    } finally {
      setUser(null);
      setCachedPermissions(null);
      apolloClient.clearStore();
    }
  };

  const switchUserContext = async (vinculoId: number) => {
    try {
      const result = await switchContext(vinculoId);

      if (result.user) {
        setUser(result.user as unknown as User);
        // Recarregar permissões após trocar contexto
        if (result.user.id) {
          await loadPermissions(String(result.user.id));
        }

        // Refetch de todas as queries ativas para atualizar dados com novo contexto
        await apolloClient.refetchQueries({
          include: 'active', // Apenas queries ativas
        });

      }
    } catch (error) {
      console.error('Erro ao trocar contexto:', error);
      throw error;
    }
  };

  const refreshPermissions = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const freshPermissions = await permissionCache.forceRefresh(Number(user.id));
      setCachedPermissions(freshPermissions);
    } catch (error) {
      console.error('❌ Error refreshing permissions:', error);
    }
  }, [user?.id]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isSuperAdmin,
    hasPermission,
    hasRole,
    getActiveVinculos,
    login,
    logout,
    switchUserContext,
    refreshPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
